/**
 * History service — `record_changes`, the audit log: one row per insert,
 * update or delete of a record or one of its rows (contact, link, listing, …),
 * written by database triggers.
 *
 * A record's history is the changes it is the PRIMARY subject of
 * (`record_id`) together with those it is the SECONDARY side of
 * (`secondary_record_id`): a join change records the other record too — the
 * linked record of a link, the list of a listing — so linking B from A shows
 * in B's history as well as A's. `historyWhere` is that rule, and every
 * per-record read here (and an interface's own, for polling) builds its filter
 * from it, so the app and the agent report the same history.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import type { RecordChangeFieldsFragment } from "../gql/generated/graphql.js"
import type { Record_Changes_Bool_Exp } from "../gql/generated/schema.js"
import {
  fetchChangesByIds,
  fetchHistoryCounts,
  fetchHistoryUsers,
  fetchRecordHistory,
  fetchRecordOwnChanges,
} from "../gql/history.js"
import type { Query } from "../index.js"

/** Narrows a record's history. An absent, null or empty filter matches everything. */
export interface HistoryFilters {
  /** Only changes to these entity types (`record`, `link`, `listing`, …). */
  entityTypes?: readonly string[] | null
  /** Only changes made by these people (`actor_person_id`). */
  personIds?: readonly number[] | null
  /** Only changes at or after this ISO timestamp. */
  startDate?: string | null
  /** Only changes at or before this ISO timestamp. */
  endDate?: string | null
}

/** A change row: RecordChangeFields. */
export type RecordChange = RecordChangeFieldsFragment

/** A page of a record's history and how many changes match in all. */
export interface HistoryPage {
  changes: RecordChange[]
  total: number
}

/** Someone who changed a record. */
export type HistoryUser = NonNullable<
  ResultOf<typeof fetchHistoryUsers>["users"][number]["actor_person"]
>

/** Changes per entity type, for the types fetchHistoryCounts counts. */
export type HistoryCounts = Record<
  "record" | "contact" | "listing" | "source" | "tagging" | "attachment" | "link",
  number
>

/** A row of `ownChanges`: a change without its before/after snapshots. */
export type OwnChange = ResultOf<typeof fetchRecordOwnChanges>["changes"][number]

/**
 * The changes that make up a record's history, narrowed by `filters`: the
 * record as either side of the change (see the module doc), and each filter
 * that is set as one more condition.
 */
export function historyWhere(
  recordId: number,
  filters: HistoryFilters = {}
): Record_Changes_Bool_Exp {
  const { entityTypes, personIds, startDate, endDate } = filters
  const where: Record_Changes_Bool_Exp = {
    _or: [{ record_id: { _eq: recordId } }, { secondary_record_id: { _eq: recordId } }],
  }
  // Only a filter that is set adds a condition: Hasura refuses `_in: null`.
  if (entityTypes?.length) where.entity_type = { _in: [...entityTypes] }
  if (personIds?.length) where.actor_person_id = { _in: [...personIds] }
  if (startDate || endDate) {
    where.created_at = {}
    if (startDate) where.created_at._gte = startDate
    if (endDate) where.created_at._lte = endDate
  }
  return where
}

export function createHistoryService({ query }: { query: Query }) {
  /** A page of the record's history, newest first. */
  const list = async (
    recordId: number,
    filters: HistoryFilters = {},
    page: { limit?: number; offset?: number } = {}
  ): Promise<HistoryPage> => {
    const data = await query(fetchRecordHistory, {
      where: historyWhere(recordId, filters),
      limit: page.limit ?? 50,
      offset: page.offset ?? 0,
    })
    // A missing aggregate means the read was refused or failed; reporting it
    // as zero would read as "no history".
    const total = data.aggregate?.aggregate?.count
    if (total == null) throw new Error(`History of record ${recordId}: no count returned`)
    return { changes: data.changes, total }
  }

  /** The number of the record's changes per entity type (HistoryCounts' keys),
   *  under the same filters `list` takes — the entity-type filter aside. */
  const counts = async (recordId: number, filters: HistoryFilters = {}): Promise<HistoryCounts> => {
    const whereFor = (type: string) => historyWhere(recordId, { ...filters, entityTypes: [type] })
    const data = await query(fetchHistoryCounts, {
      record: whereFor("record"),
      contact: whereFor("contact"),
      listing: whereFor("listing"),
      source: whereFor("source"),
      tagging: whereFor("tagging"),
      attachment: whereFor("attachment"),
      link: whereFor("link"),
    })
    // Every count must be there: a missing one is a refused or failed read, not zero.
    const count = (type: keyof HistoryCounts): number => {
      const n = data[type]?.aggregate?.count
      if (n == null) throw new Error(`History of record ${recordId}: no '${type}' count returned`)
      return n
    }
    return {
      record: count("record"),
      contact: count("contact"),
      listing: count("listing"),
      source: count("source"),
      tagging: count("tagging"),
      attachment: count("attachment"),
      link: count("link"),
    }
  }

  /** The distinct people who changed the record (changes with no person aside). */
  const users = async (recordId: number): Promise<HistoryUser[]> => {
    const data = await query(fetchHistoryUsers, {
      where: { _and: [historyWhere(recordId), { actor_person_id: { _is_null: false } }] },
    })
    return data.users.flatMap((u) => (u.actor_person ? [u.actor_person] : []))
  }

  /** Change rows by id, newest first. Ids that match nothing are left out. */
  const get = async (ids: readonly number[]): Promise<RecordChange[]> => {
    if (ids.length === 0) return []
    const data = await query(fetchChangesByIds, { ids: [...ids] })
    return data.changes
  }

  /** Every change the record is the primary subject of, oldest first, without
   *  before/after snapshots. */
  const ownChanges = async (recordId: number): Promise<OwnChange[]> => {
    const data = await query(fetchRecordOwnChanges, { recordId })
    return data.changes
  }

  return { list, counts, users, get, ownChanges }
}
